import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, money0, shortDate } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { KpiCard, StatusPill, AlertRow } from "../components/UI";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then(r => setData(r.data));
    api.get("/drafts").then(r => setDrafts(r.data.drafts || [])).catch(()=>{});
  }, []);

  if (!data) return <div style={{padding: 40, color:"var(--ach-text-2)"}}>Loading…</div>;

  const k = data.kpis;
  // Every KPI can be null when a Director has hidden it from this user's role.
  // Whole cards (coming_back, decisions, month_end_progress) can be null too.
  const vis = data.visibility || {};

  const resumeDraft = (d) => {
    if (d.kind === "hire") nav(`/start-a-hire?draft=${d.id}`);
    else if (d.kind === "customer") nav(`/new-customer?draft=${d.id}`);
    else nav(`/quotes?draft=${d.id}`);
  };
  const deleteDraft = async (id) => {
    if (!window.confirm("Delete this draft? This can't be undone.")) return;
    await api.delete(`/drafts/${id}`);
    api.get("/drafts").then(r => setDrafts(r.data.drafts || []));
  };

  return (
    <div data-testid="dashboard-page">
      {data.dry_run_active && (
        <div style={{
          padding: "12px 16px", marginBottom: 16, borderRadius: 8,
          background: "repeating-linear-gradient(45deg, #FFEBB0, #FFEBB0 10px, #FFE08A 10px, #FFE08A 20px)",
          border: "2px solid var(--ach-amber, #c88c13)",
          color: "#5c3b00", fontSize: 13, fontWeight: 500,
          display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
        }} data-testid="dry-run-banner">
          <span style={{fontSize:16}}>🧪</span>
          <strong>DRY RUN MODE ACTIVE</strong>
          <span>Nothing you do here hits Xero or sends emails to customers. Invoice numbers will be <code>INV-DRY-XXXX</code>. Rehearse the full month-end flow safely.</span>
        </div>
      )}
      <PageHeader
        title="Dashboard"
        subtitle={`${data.month_label} · up to date with Xero`}
        actions={
          <>
            <button className="ach-btn ach-btn--ghost" onClick={()=>nav("/quotes")} data-testid="btn-new-quote">+ New quote</button>
            <button className="ach-btn ach-btn--ghost" onClick={()=>nav("/start-a-hire")} data-testid="btn-start-hire">+ Start a hire</button>
            <button className="ach-btn ach-btn--primary" onClick={()=>nav("/month-end")} data-testid="btn-start-month-end">Start month-end</button>
          </>
        }
      />

      <div className="ach-kpi-row" data-testid="kpi-row">
        {/*
          Dashboard KPI tiles (Friction Batch e, Sep 2026). Each interactive
          tile takes the user straight to the page that answers the question
          the number raises: "20 machines in the fleet" → the Machines list,
          "$244k of charges this month" → the current month-end page, etc.
          The Money-owed tile is deliberately inert (nowhere better to land
          than the Money-owed table itself, and Todd asked for it to stay
          as a summary read).
        */}
        {k.fleet_count != null && <KpiCard tone="neutral" number={k.fleet_count} label="Machines in the fleet" testid="kpi-fleet" onClick={()=>nav("/machines")} />}
        {k.on_hire_count != null && <KpiCard tone="green" number={k.on_hire_count} label="Out earning right now" testid="kpi-on-hire" onClick={()=>nav("/hires")} />}
        {k.total_this_month != null && <KpiCard tone="maroon" number={money0(k.total_this_month)} label={`Charges for ${data.month_label}`} testid="kpi-charges" onClick={()=>nav("/month-end")} />}
        {k.money_owed != null && <KpiCard tone="amber" number={money0(k.money_owed)} label="Money owed to us" testid="kpi-money-owed" />}
        {k.papers_out_of_date != null && k.papers_due_soon != null && (
          <KpiCard tone={k.papers_out_of_date > 0 ? "red" : "amber"} number={k.papers_out_of_date + k.papers_due_soon} label="Rego / inspections to sort" testid="kpi-papers" onClick={()=>nav("/rego-inspections")} />
        )}
        {k.review_needed != null && <KpiCard tone="amber" number={k.review_needed} label="Charges that need checking" testid="kpi-review" onClick={()=>nav("/month-end")} />}
        {k.po_expiring_count != null && <KpiCard tone={k.po_expiring_count > 0 ? "amber" : "neutral"} number={k.po_expiring_count} label="POs expiring within 30 days" testid="kpi-po-expiry" onClick={()=>nav("/hires?filter=po-expiring")} />}
      </div>

      <div className="ach-2col">
        <div>
          <div className="ach-card" style={{marginBottom: 16}} data-testid="card-on-hire">
            <h2 className="ach-card__title">On hire right now <small>sorted by needs-attention first</small></h2>
            <div className="ach-table-wrap">
              <table className="ach-table">
                <thead>
                  <tr>
                    <th>Machine</th>
                    <th>Model</th>
                    <th>Who has it</th>
                    <th>Where</th>
                    <th>Status</th>
                    {vis.on_hire_this_month_col && <th className="money">This month</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.on_hire_rows.map(r => (
                    <tr key={r.hire_id} data-testid={`onhire-row-${r.unit_number}`}>
                      <td>
                        <div className="unit-num">{r.unit_number}</div>
                        <div className="subtitle">{r.owning_company}</div>
                      </td>
                      <td>{r.model}</td>
                      <td>{r.customer_name}</td>
                      <td>{r.location || "—"}</td>
                      <td><StatusPill tone={r.status_tone}>{r.status_label}</StatusPill></td>
                      {vis.on_hire_this_month_col && <td className="money">{money(r.this_month)}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {vis.on_hire_this_month_col && (
              <div style={{fontSize:11, color:"var(--ach-text-2)", marginTop:10}}>
                Amounts are worked out for you from the weekly rate × 4.33 (our standard). Anything unusual is flagged for a look before month-end.
              </div>
            )}
          </div>

          {data.coming_back && (
          <div className="ach-card" data-testid="card-coming-back">
            <h2 className="ach-card__title">Coming back / free soon</h2>
            {data.coming_back.length === 0 ? (
              <div style={{fontSize:12, color:"var(--ach-text-2)"}}>Nothing coming back in the next three weeks.</div>
            ) : data.coming_back.map((r, i) => (
              <AlertRow
                key={i}
                tone="green"
                icon="✓"
                title={`${r.unit_number} — ${r.model}`}
                sub={r.match_customer ? r.match_customer : (r.customer_name + ` · back ${shortDate(r.return_date)} (${r.days_until} days)`)}
                actionText={r.match_customer ? "Show in yard →" : "Quote them →"}
                onAction={() => nav(r.match_customer ? "/machines" : "/quotes")}
                testid={`coming-back-${r.unit_number}`}
              />
            ))}
          </div>
          )}
        </div>

        <div>
          {data.decisions && (
          <div className="ach-card" style={{marginBottom: 16}} data-testid="card-decisions">
            <h2 className="ach-card__title">Needs a decision today</h2>
            {data.decisions.length === 0 ? (
              <div style={{fontSize:12, color:"var(--ach-text-2)"}}>All clear. Nice one.</div>
            ) : data.decisions.map(d => (
              <AlertRow
                key={d.id}
                tone={d.tone}
                title={d.title}
                sub={d.subtitle}
                actionText={d.action_text}
                onAction={() => {
                  const href = d.action_href || "";
                  // Deep-link CraneSafe/rego decisions to /rego-inspections with the machine highlighted.
                  if (href.startsWith("/rego-inspections") && d.subtitle) {
                    const match = /(ACH\d+)/i.exec(d.subtitle) || /(ACH\d+)/i.exec(d.title || "");
                    if (match) { nav(`/rego-inspections?unit=${match[1]}`); return; }
                  }
                  nav(href);
                }}
                testid={`decision-${d.id}`}
              />
            ))}
          </div>
          )}

          {drafts.length > 0 && (
            <div className="ach-card" style={{marginBottom:16}} data-testid="card-drafts">
              <h2 className="ach-card__title">Saved drafts <small>things you started but haven&apos;t finished</small></h2>
              {drafts.map(d => (
                <div key={d.id} style={{display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 0", borderBottom:"1px solid var(--ach-row-divider)"}} data-testid={`draft-${d.id}`}>
                  <div>
                    <div style={{fontWeight:600, fontSize:13}}>{d.title}</div>
                    <div style={{fontSize:11, color:"var(--ach-text-2)"}}>{d.kind} · saved {new Date(d.updated_at).toLocaleDateString("en-AU")}</div>
                  </div>
                  <div style={{display:"flex", gap:6}}>
                    <button className="ach-btn ach-btn--grey" style={{padding:"4px 10px", fontSize:11}} onClick={()=>resumeDraft(d)} data-testid={`draft-resume-${d.id}`}>Resume →</button>
                    <button className="ach-btn ach-btn--ghost" style={{padding:"4px 8px", fontSize:11, color:"var(--ach-red)"}} onClick={()=>deleteDraft(d.id)} data-testid={`draft-delete-${d.id}`}>×</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {data.month_end_progress && (
          <div className="ach-card" data-testid="card-month-end-progress">
            <h2 className="ach-card__title">Month-end billing · {data.month_end_progress.month_label}</h2>
            <div className="ach-progress-row">
              <div className="ach-progress-row__label">
                <span>Charges worked out for you</span>
                <strong>{data.month_end_progress.total_lines}</strong>
              </div>
              <div className="ach-progress"><div className="ach-progress__fill" style={{width: "100%"}} /></div>
            </div>
            <div className="ach-progress-row">
              <div className="ach-progress-row__label">
                <span>Everything looks fine</span>
                <strong>{data.month_end_progress.everything_ok_lines}</strong>
              </div>
              <div className="ach-progress"><div className="ach-progress__fill green" style={{width: `${100 * data.month_end_progress.everything_ok_lines / Math.max(1, data.month_end_progress.total_lines)}%`}} /></div>
            </div>
            <div className="ach-progress-row">
              <div className="ach-progress-row__label">
                <span>Needs checking</span>
                <strong>{data.month_end_progress.needs_checking_lines}</strong>
              </div>
              <div className="ach-progress"><div className="ach-progress__fill amber" style={{width: `${100 * data.month_end_progress.needs_checking_lines / Math.max(1, data.month_end_progress.total_lines)}%`}} /></div>
            </div>
            <button className="ach-btn ach-btn--primary" style={{width:"100%", justifyContent:"center", marginTop:8}}
              onClick={()=>nav("/month-end")} data-testid="btn-check-approve">
              Check &amp; approve →
            </button>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
